import { RINGS, SURFACE_NAME_MAX_LENGTH, SURFACE_NAME_PATTERN, appVersionStringSchema, ringSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/root-surface-settings.schemas.ts
/**
* The wire shape of release-engine `GET /internal/v1/apps/:slug/root-surface-settings` (layered
* shell surfaces, R-59, R-69).
*
* platform-api's `PUT /v1/orgs/:orgId/shell` (WS-28) checks a tenant's shell setting against every
* version a root shell app has pinned on a public ring, because the CP does not know which ring a
* tenant's data plane follows (R-33, R-35). release-engine (WS-4) owns the pins and the stored
* manifests, and answers this route with one row per public-ring pin. This file is the one home of
* that row (R-69): the producer's DTO and the consumer's reader import both schemas from here, and
* neither declares the shape again.
*
* The row is `.strict()` on both sides. The producer can send no key the consumer does not read,
* and a consumer that parses a later key it does not know refuses the body. A new member is a
* contract change in this file, never a silent addition in one service.
*
* No `.transform()` appears here, so both schemas stay representable for `createOpenApiDocument()`.
* The two refines below are dropped from the JSON Schema and still run on every parse.
*
* This module does no I/O and holds no state.
*/
/**
* The most rows one response holds: one per public ring of `RINGS` (`stable`, `beta`, `alpha`,
* `edge`), so 4. A longer body is the wrong shape, so a faulty producer cannot make one `PUT`
* compile an unbounded number of settings validators.
*/
const MAX_ROOT_SURFACE_SETTINGS_ROWS = RINGS.length;
/** The message a row with no root surface and a non-null `settings` earns. */
const ROOT_SURFACE_SETTINGS_ORPHAN_MESSAGE = "settings must be null when the row names no root surface";
/** The message a response with two rows on one ring earns. */
const ROOT_SURFACE_SETTINGS_DUPLICATE_RING_MESSAGE = "a response holds at most one row per public ring";
/**
* One public-ring pin of a root shell app, with what its pinned version's root surface declares.
*
* - `version`: the pinned composition's `app_version`. `appVersionStringSchema`: 1 to 128
*   characters, the bound the CP-to-operator wire carries for an app version.
* - `ring`: the public ring that pins it, one of `stable`, `beta`, `alpha`, `edge` (`ringSchema`).
* - `surface`: the `name` of the pinned version's first `surfaces[]` row with `root: true`, in the
*   surface-name grammar (`SURFACE_NAME_PATTERN`, at most 64 characters). The JSON null when that
*   version declares no root surface: a version published before `surfaces[]` existed, or one
*   with no `root: true` row. platform-api refuses with 400 a `rootShell.surface` that is not the
*   `surface` of every row (R-69), so a `null` row refuses every `rootShell` for that app until the
*   ring pins a version with a root surface.
* - `settings`: that root surface's declared `settings` schema, a JSON Schema object in the closed
*   type language (R-35) as the publish door stored it. The values are opaque here. The JSON null
*   when the version declares no root surface, when its root surface declares no `settings`, or
*   when the stored value is not a JSON object.
*
* Every key is required. A row whose `surface` is `null` must carry `settings: null`; a row that
* names no root surface and carries a schema is refused with
* {@link ROOT_SURFACE_SETTINGS_ORPHAN_MESSAGE}. Any other key is refused (`.strict()`).
*/
const rootSurfaceSettingsRowSchema = z.object({
	version: appVersionStringSchema,
	ring: ringSchema,
	surface: z.string().max(SURFACE_NAME_MAX_LENGTH).regex(new RegExp(SURFACE_NAME_PATTERN)).nullable(),
	settings: z.record(z.string(), z.unknown()).nullable()
}).strict().refine((row) => row.surface !== null || row.settings === null, {
	message: ROOT_SURFACE_SETTINGS_ORPHAN_MESSAGE,
	path: ["settings"]
});
/**
* The 200 body: a bare array of rows, at most one per public ring, so at most
* {@link MAX_ROOT_SURFACE_SETTINGS_ROWS}. `[]` when the app is registered and no public ring pins a
* version of it. release-engine answers 404 with no body when it does not know the app, and that
* answer is not this schema's.
*
* The producer sends the rows in the order of `RINGS`. The schema does not check the order. It
* refuses a body of more than {@link MAX_ROOT_SURFACE_SETTINGS_ROWS} rows, and a body with two
* rows on one ring, with {@link ROOT_SURFACE_SETTINGS_DUPLICATE_RING_MESSAGE}.
*/
const rootSurfaceSettingsResponseSchema = z.array(rootSurfaceSettingsRowSchema).max(MAX_ROOT_SURFACE_SETTINGS_ROWS).refine((rows) => new Set(rows.map((row) => row.ring)).size === rows.length, { message: ROOT_SURFACE_SETTINGS_DUPLICATE_RING_MESSAGE });
//#endregion
export { MAX_ROOT_SURFACE_SETTINGS_ROWS, ROOT_SURFACE_SETTINGS_DUPLICATE_RING_MESSAGE, ROOT_SURFACE_SETTINGS_ORPHAN_MESSAGE, rootSurfaceSettingsResponseSchema, rootSurfaceSettingsRowSchema };

//# sourceMappingURL=root-surface-settings.schemas.js.map